import { Button, CircularProgress, Typography } from "@suraasa/placebo-ui"
import api from "api"
import { GlobalContext } from "components/GlobalState"
import { WarningTriangle } from "iconoir-react"
import { useContext, useEffect, useState } from "react"
import { Navigate, Outlet } from "react-router-dom"
import { AuthManager } from "utils/AuthManager"
import { routes } from "utils/routes"

const EQA_ONLY_MODE = import.meta.env.VITE_EQA_ONLY_MODE === "true"

function getEmailBlockMessage(email: string): string | null {
  const lower = email.toLowerCase()
  const isEqaEmail =
    lower.startsWith("eqa") && lower.split("@")[1] === "suraasa.com"

  if (EQA_ONLY_MODE) {
    if (!isEqaEmail) {
      return `You cannot access the EQA platform with this account: ${email}`
    }
  } else {
    if (lower.startsWith("eqa")) {
      return `You cannot access the Professor platform with this account: ${email}`
    }
  }

  return null
}

const PrivateRoute = () => {
  const [authIsValid, setAuthIsValid] = useState<boolean>(false)
  const [blockMessage, setBlockMessage] = useState<string | null>(null)

  const { setProfile } = useContext(GlobalContext)
  const auth = AuthManager.getAuthInfo()

  useEffect(() => {
    const checkAuth = async () => {
      const authInfo = AuthManager.getAuthInfo()
      try {
        if (authInfo) {
          const res = await api.auth.verifyAccessToken({
            data: {
              token: authInfo.accessToken,
            },
            headers: {
              Authorization: null,
            },
          })

          if (res.verified) {
            const user = await api.auth.getUser()
            const message = getEmailBlockMessage(user.email)
            if (message) {
              setBlockMessage(message)
            } else {
              setAuthIsValid(true)
            }
          }
        }
      } catch (e) {
        console.log("> Access token is invalid", e)
      }
    }

    if (AuthManager.getAuthInfo()) {
      checkAuth()
    } else {
      setAuthIsValid(true)
    }
  }, [])

  useEffect(() => {
    if (auth) {
      setProfile(auth.user)
    }
  }, [])

  if (blockMessage) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-surface-50 p-5 text-center">
        <div className="mb-2 flex size-14 items-center justify-center rounded-full bg-critical-100">
          <WarningTriangle className="size-7 text-critical-500" />
        </div>
        <Typography variant="title3" className="my-2">
          Access Denied
        </Typography>
        <Typography className="max-w-sm text-muted">{blockMessage}</Typography>
        <Button
          className="mt-6"
          variant="outlined"
          onClick={() => AuthManager.logout()}
        >
          Sign in with a different account
        </Button>
      </div>
    )
  }

  if (auth && !authIsValid) {
    return (
      <div className="flex h-screen w-full items-center justify-center p-5">
        <CircularProgress size="md" />
      </div>
    )
  }

  if (auth && authIsValid) {
    return <Outlet />
  }

  return (
    <Navigate
      to={{
        pathname: routes.root,
        search: `?next=${window.location.pathname}`,
      }}
      replace
    />
  )
}

export default PrivateRoute
